/**
 * Help pages: the Markdown in `apps/pos/help/`, read into something the panel
 * can show, search and filter (docs/HELP.md, decision 160).
 *
 * Pure, and tested here rather than through a screen. The Markdown is a small
 * subset on purpose — headings, paragraphs, lists, bold, italics, `[[links]]`
 * — so what a page can say is what the panel can show, and nothing in a page
 * is ever inserted as HTML.
 */

/** The places in the POS a task can start. One per top-level section. */
export const HELP_SCREENS = [
  'register',
  'sales',
  'online',
  'customers',
  'repairs',
  'stock',
  'reports',
  'settings',
] as const;
export type HelpScreen = (typeof HELP_SCREENS)[number];

/** How pages are grouped in the panel, in the order it lists them. */
export const HELP_SECTIONS = [
  { key: 'getting-started', label: 'Getting started' },
  { key: 'register', label: 'The register' },
  { key: 'sales', label: 'Sales and returns' },
  { key: 'online', label: 'Online orders' },
  { key: 'customers', label: 'Customers' },
  { key: 'repairs', label: 'Repairs' },
  { key: 'stock', label: 'Stock and buying' },
  { key: 'web-shop', label: 'The web shop' },
  { key: 'reports', label: 'Reports and end of day' },
  { key: 'settings', label: 'Setting up the store' },
] as const;
export type HelpSection = (typeof HELP_SECTIONS)[number]['key'];

/** A task may take at most this many steps; more is two tasks (docs/HELP.md). */
export const MAX_STEPS = 8;

export type Inline =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'bold'; readonly text: string }
  | { readonly kind: 'italic'; readonly text: string }
  | { readonly kind: 'link'; readonly id: string };

export type Block =
  | { readonly kind: 'heading'; readonly text: string }
  | { readonly kind: 'paragraph'; readonly content: readonly Inline[] }
  | { readonly kind: 'steps'; readonly items: readonly (readonly Inline[])[] }
  | { readonly kind: 'list'; readonly items: readonly (readonly Inline[])[] };

/**
 * The tabs *Take me there* can land on, by screen, with the name each shows.
 * A page whose task lives on one tab says so (`tab: counts`): "Take me there —
 * Stock" landed on Products, and a reviewer looking for a count pressed
 * around a style instead of finding Counts (FB-1083).
 */
export const HELP_TABS: Readonly<Partial<Record<HelpScreen, Readonly<Record<string, string>>>>> = {
  stock: {
    products: 'Products',
    categories: 'Categories',
    purchaseOrders: 'Purchase orders',
    backorders: 'Backorders',
    vendors: 'Vendors',
    import: 'Import',
    counts: 'Counts',
  },
  reports: {
    endOfDay: 'End of day',
    custom: 'Custom reports',
    sales: 'Sales',
    inventory: 'Inventory',
    modelGrid: 'Model grid',
    openToBuy: 'Open to buy',
    onOrder: 'On order',
    receiving: 'Receiving',
    adjustments: 'Adjustments',
    reorder: 'Reorder',
    discounts: 'Discounts',
    giftCards: 'Gift cards',
    activity: 'Activity',
  },
};

export interface HelpPage {
  readonly id: string;
  readonly title: string;
  readonly section: HelpSection;
  readonly screen: HelpScreen;
  /** The tab of that screen the task is on, when it is on one (`HELP_TABS`). */
  readonly tab: string | null;
  readonly summary: string;
  /** The permission that governs the task; absent when anyone signed in may. */
  readonly permission: string | null;
  /** Capabilities the store must have: `dual-pricing`, `shipping`. */
  readonly requires: readonly string[];
  /** The words people use for this task, which search also matches. */
  readonly synonyms: readonly string[];
  readonly blocks: readonly Block[];
  /** Every `[[id]]` the page links to, for the check that they exist. */
  readonly links: readonly string[];
  /** Every **bold** word: on-screen names, checked against the screens. */
  readonly bold: readonly string[];
  /** How many numbered steps the page has. */
  readonly stepCount: number;
}

export class HelpPageError extends Error {
  constructor(file: string, problem: string) {
    super(`${file}: ${problem}`);
    this.name = 'HelpPageError';
  }
}

/** `**bold**`, `*italic*` and `[[page-id]]`, in the order they appear. */
export function inline(text: string): Inline[] {
  const parts: Inline[] = [];
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*|\[\[([a-z0-9-]+)\]\]/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > last) parts.push({ kind: 'text', text: text.slice(last, match.index) });
    if (match[1] !== undefined) parts.push({ kind: 'bold', text: match[1] });
    else if (match[2] !== undefined) parts.push({ kind: 'italic', text: match[2] });
    else parts.push({ kind: 'link', id: match[3]! });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts;
}

function frontmatter(file: string, source: string): { fields: Map<string, string>; body: string } {
  const normalized = source.replace(/\r\n/g, '\n');
  if (!normalized.startsWith('---\n')) throw new HelpPageError(file, 'no frontmatter');
  const end = normalized.indexOf('\n---', 4);
  if (end === -1) throw new HelpPageError(file, 'frontmatter is not closed');
  const fields = new Map<string, string>();
  for (const line of normalized.slice(4, end).split('\n')) {
    if (line.trim() === '') continue;
    const colon = line.indexOf(':');
    if (colon === -1) throw new HelpPageError(file, `not a field: "${line}"`);
    fields.set(line.slice(0, colon).trim(), line.slice(colon + 1).trim());
  }
  return { fields, body: normalized.slice(end + 4) };
}

function listField(value: string | undefined): string[] {
  if (value === undefined || value === '') return [];
  const inner = value.replace(/^\[/, '').replace(/\]$/, '');
  return inner
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

interface OpenList {
  kind: 'steps' | 'list';
  items: string[];
}

function blocks(body: string): Block[] {
  const out: Block[] = [];
  let paragraph: string[] = [];
  let list: OpenList | null = null;

  const flushParagraph = (): void => {
    if (paragraph.length > 0) out.push({ kind: 'paragraph', content: inline(paragraph.join(' ')) });
    paragraph = [];
  };
  const flushList = (): void => {
    if (list !== null) out.push({ kind: list.kind, items: list.items.map(inline) });
    list = null;
  };

  for (const raw of body.split('\n')) {
    const line = raw.trimEnd();
    const heading = /^##\s+(.+)$/.exec(line);
    const step = /^\d+\.\s+(.+)$/.exec(line);
    const bullet = /^[-*]\s+(.+)$/.exec(line);

    if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else if (heading !== null) {
      flushParagraph();
      flushList();
      out.push({ kind: 'heading', text: heading[1]! });
    } else if (step !== null || bullet !== null) {
      flushParagraph();
      const kind = step !== null ? 'steps' : 'list';
      const text = (step ?? bullet)![1]!;
      if (list === null || list.kind !== kind) {
        flushList();
        list = { kind, items: [text] };
      } else {
        list.items.push(text);
      }
    } else if (/^\s+\S/.test(raw) && list !== null) {
      // A wrapped list item continues the one above it.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return out;
}

function inlinesOf(block: Block): readonly Inline[] {
  switch (block.kind) {
    case 'paragraph':
      return block.content;
    case 'steps':
    case 'list':
      return block.items.flat();
    case 'heading':
      return [];
  }
}

/** Read one page. Throws, naming the file, when the page is not well-formed. */
export function parsePage(file: string, source: string): HelpPage {
  const { fields, body } = frontmatter(file, source);
  const required = (key: string): string => {
    const value = fields.get(key);
    if (value === undefined || value === '') throw new HelpPageError(file, `needs "${key}"`);
    return value;
  };

  const id = required('id');
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)) {
    throw new HelpPageError(file, `id "${id}" must be lower-case words joined by hyphens`);
  }
  const section = required('section');
  if (!HELP_SECTIONS.some((entry) => entry.key === section)) {
    throw new HelpPageError(file, `section "${section}" is not one of the panel's sections`);
  }
  const screen = required('screen');
  if (!(HELP_SCREENS as readonly string[]).includes(screen)) {
    throw new HelpPageError(file, `screen "${screen}" is not a screen of the POS`);
  }

  const tab = fields.get('tab') || null;
  if (tab !== null && HELP_TABS[screen as HelpScreen]?.[tab] === undefined) {
    throw new HelpPageError(file, `tab "${tab}" is not a tab of ${screen}`);
  }

  const parsed = blocks(body);
  const every = parsed.flatMap(inlinesOf);
  const steps = parsed.filter((block) => block.kind === 'steps');
  const stepCount = steps.reduce((sum, block) => sum + block.items.length, 0);

  return {
    id,
    title: required('title'),
    section: section as HelpSection,
    screen: screen as HelpScreen,
    tab,
    summary: required('summary'),
    permission: fields.get('permission') || null,
    requires: listField(fields.get('requires')),
    synonyms: listField(fields.get('synonyms')),
    blocks: parsed,
    links: every.filter((part) => part.kind === 'link').map((part) => part.id),
    bold: every
      .filter((part) => part.kind === 'bold')
      .map((part) => (part as { text: string }).text),
    stepCount,
  };
}

/** Who is reading, and what their store has. */
export interface HelpReader {
  readonly permissions: ReadonlySet<string>;
  readonly capabilities: ReadonlySet<string>;
}

/** Whether a page is for this person (criterion 5): pages for what they cannot do are hidden. */
export function isForReader(page: HelpPage, reader: HelpReader): boolean {
  if (page.permission !== null && !reader.permissions.has(page.permission)) return false;
  return page.requires.every((capability) => reader.capabilities.has(capability));
}
